import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

import { Link } from "react-router-dom";
import { ChevronRight, Zap, Target, Clock, ShieldCheck, Activity, CheckCircle2, AlertCircle } from "lucide-react";
import { SEOHead } from "@/components/SEOHead";

export default function Softwave() {
  return (
    <div className="min-h-screen bg-background font-sans text-foreground flex flex-col">
      <SEOHead 
        title="SoftWave TRT | Woods Spine & Wellness | Longview TX"
        description="Experience advanced healing with SoftWave TRT Tissue Regeneration Technology at Woods Spine & Wellness in Longview, TX. Non-invasive pain relief."
        ogImage="https://softwavetrt.com/wp-content/uploads/2026/02/softwave099-1.png"
        canonical="https://woodsspine.com/longview-softwave-pain-management-tissue-regeneration"
      />
      <Navbar />
      <main className="flex-grow pt-24 pb-16">
        <section className="container mx-auto px-4 mb-16">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              <div className="inline-flex items-center rounded-full border bg-secondary/50 px-3 py-1 text-sm font-medium text-primary mb-2">
                <Zap className="mr-2 h-4 w-4 text-accent" />
                Advanced Healing Technology
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-primary leading-tight">
                SoftWave TRT <br />
                <span className="text-accent">Tissue Regeneration</span>
              </h1>
              <p className="text-xl text-muted-foreground leading-relaxed">
                Built to be the Gold Standard in regenerative care—electrohydraulic shockwaves delivered with precision, power, and proof.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <Button size="lg" className="bg-accent text-white hover:bg-accent/90" asChild>
                  <Link to="/appointment-scheduling">
                    Book Your Consultation <ChevronRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              </div>
            </div>
            <div className="relative">
              <div className="absolute inset-0 bg-primary/5 rounded-3xl transform rotate-3"></div>
              <div className="bg-white p-8 rounded-3xl shadow-xl relative z-10 border border-border flex items-center justify-center min-h-[400px]">
                <img 
                  src="https://wsrv.nl/?url=https://softwavetrt.com/wp-content/uploads/2026/02/softwave099-1.png&w=1280&output=webp&q=80" 
                  alt="SoftWave TRT Device" 
                  className="max-w-full h-auto object-contain drop-shadow-2xl hover:scale-105 transition-transform duration-500"
                  fetchPriority="high"
                  decoding="async"
                />
              </div>
            </div>
          </div>
        </section>

        {/* WHAT IT IS */}
        <section className="py-16 bg-secondary/30">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">What is SoftWave TRT?</h2>
            <div className="prose prose-lg text-muted-foreground">
              <p>
                SoftWave Tissue Regeneration Technology (TRT) is a cutting-edge, non-invasive therapy designed to stimulate your body's natural healing capabilities. Unlike traditional treatments that merely mask pain with medication or require invasive surgical procedures, SoftWave addresses the root cause of your discomfort by promoting cellular repair and regeneration at the microscopic level.
              </p>
              <p>
                This advanced therapy utilizes patented electrohydraulic shockwaves—also known as acoustic waves—to penetrate deep into damaged tissues. These unfocused waves cover a broad area, ensuring that the entire injured region receives the therapeutic benefits. SoftWave is FDA-cleared and has been widely adopted by top medical professionals, professional sports teams, and premier clinics across the country.
              </p>
              <p>
                By triggering a biological response that reduces inflammation, increases blood flow, and recruits resident stem cells to the area of injury, SoftWave TRT helps patients achieve rapid pain relief and long-term healing. It is a safe, effective, and painless alternative for those suffering from acute injuries or chronic pain conditions.
              </p>
            </div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">How SoftWave Therapy Works</h2>
            <p className="text-lg text-muted-foreground mb-8">
              The science behind SoftWave TRT is rooted in its unique method of delivering acoustic waves to damaged tissue. Here is a step-by-step look at how the technology facilitates healing:
            </p>
            
            <div className="grid gap-6">
              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6 flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                    <span className="font-bold text-xl">1</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Acoustic Wave Delivery</h3>
                    <p className="text-muted-foreground">The SoftWave device generates electrohydraulic shockwaves that travel through the skin and into the underlying tissue. Because the waves are unfocused, they spread out over a large area, minimizing discomfort while maximizing the treatment zone.</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6 flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                    <span className="font-bold text-xl">2</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Cellular Activation</h3>
                    <p className="text-muted-foreground">As the acoustic waves pass through the tissue, they create a physical force that "tricks" the body into thinking an injury has occurred, without actually causing any damage. This stimulates a profound biological response at the cellular level.</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6 flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                    <span className="font-bold text-xl">3</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Angiogenesis & Blood Flow</h3>
                    <p className="text-muted-foreground">The therapy promotes angiogenesis—the formation of new blood vessels. This significantly increases blood flow to the damaged area, delivering essential oxygen and nutrients required for tissue repair.</p>
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6 flex gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0">
                    <span className="font-bold text-xl">4</span>
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-primary mb-2">Stem Cell Recruitment</h3>
                    <p className="text-muted-foreground">Perhaps the most powerful effect of SoftWave TRT is the activation and recruitment of resident stem cells. These cells migrate to the treated area to replace damaged tissue, accelerating the healing of muscles, tendons, ligaments, and bone.</p>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* WHO IT'S FOR */}
        <section className="py-16 bg-secondary/30">
          <div className="container mx-auto px-4 max-w-5xl">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-10 text-center">Who is SoftWave For?</h2>
            
            <div className="grid md:grid-cols-2 gap-12">
              <div>
                <h3 className="text-2xl font-bold text-primary mb-6 flex items-center">
                  <CheckCircle2 className="h-6 w-6 text-accent mr-2" /> Ideal Candidates
                </h3>
                <p className="text-muted-foreground mb-6">
                  SoftWave TRT is highly effective for patients suffering from acute injuries, chronic pain, or degenerative conditions who want to avoid surgery and reduce their reliance on pain medications. It is commonly used to treat:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-accent mr-2 shrink-0 mt-0.5" /> Plantar Fasciitis and Heel Spurs</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-accent mr-2 shrink-0 mt-0.5" /> Knee Pain, Meniscus Tears, and Arthritis</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-accent mr-2 shrink-0 mt-0.5" /> Shoulder Pain and Rotator Cuff Injuries</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-accent mr-2 shrink-0 mt-0.5" /> Tennis Elbow and Golfer's Elbow</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-accent mr-2 shrink-0 mt-0.5" /> Neuropathy and Nerve Pain</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-accent mr-2 shrink-0 mt-0.5" /> Lower Back and Neck Pain</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-2xl font-bold text-primary mb-6 flex items-center">
                  <AlertCircle className="h-6 w-6 text-destructive mr-2" /> Contraindications
                </h3>
                <p className="text-muted-foreground mb-6">
                  While SoftWave is safe for most patients, there are certain situations where the treatment is not recommended. You may not be a candidate if you:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-destructive mr-2 shrink-0 mt-0.5" /> Are currently pregnant (treatment over the abdomen/pelvis)</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-destructive mr-2 shrink-0 mt-0.5" /> Have a pacemaker or implanted electrical device in the treatment area</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-destructive mr-2 shrink-0 mt-0.5" /> Have active cancer or tumors in the treatment area</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-destructive mr-2 shrink-0 mt-0.5" /> Have a blood clotting disorder or are on heavy blood thinners</li>
                  <li className="flex items-start text-muted-foreground"><ChevronRight className="h-5 w-5 text-destructive mr-2 shrink-0 mt-0.5" /> Have an active infection in the treatment area</li>
                </ul>
                <p className="text-sm text-muted-foreground mt-4 italic">
                  *A comprehensive consultation with our doctors will determine if SoftWave is the right choice for your specific health profile.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* WHAT TO EXPECT */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-4xl">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">What to Expect During Your Session</h2>
            <div className="prose prose-lg text-muted-foreground mb-8">
              <p>
                Your comfort and understanding are our top priorities. Here is what a typical SoftWave TRT experience looks like at our clinic:
              </p>
            </div>
            
            <div className="grid sm:grid-cols-2 gap-8">
              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6">
                  <Clock className="h-8 w-8 text-accent mb-4" />
                  <h3 className="text-xl font-bold text-primary mb-2">Duration</h3>
                  <p className="text-muted-foreground">Each SoftWave session is remarkably quick, typically lasting only 10 to 15 minutes depending on the size of the area being treated. It easily fits into a busy schedule.</p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6">
                  <Activity className="h-8 w-8 text-accent mb-4" />
                  <h3 className="text-xl font-bold text-primary mb-2">What You'll Feel</h3>
                  <p className="text-muted-foreground">A conductive gel is applied to the skin, and the applicator is gently moved over the area. You will hear a clicking sound and feel a mild, pulsing sensation. If we pass over damaged tissue, you may feel a slight ache or "zing," which helps us pinpoint the exact source of your pain.</p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6">
                  <Target className="h-8 w-8 text-accent mb-4" />
                  <h3 className="text-xl font-bold text-primary mb-2">Recommended Sessions</h3>
                  <p className="text-muted-foreground">While many patients experience a noticeable reduction in pain after just one session, a full treatment protocol usually consists of 6 to 8 sessions over a few weeks to achieve maximum tissue regeneration and long-lasting results.</p>
                </CardContent>
              </Card>

              <Card className="bg-card border-border shadow-sm">
                <CardContent className="p-6">
                  <ShieldCheck className="h-8 w-8 text-accent mb-4" />
                  <h3 className="text-xl font-bold text-primary mb-2">Recovery & Downtime</h3>
                  <p className="text-muted-foreground">There is zero downtime required. You can return to your normal daily activities immediately after your session. Some patients may experience slight redness or mild soreness in the treated area, which typically resolves within a day.</p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* LOCAL RELEVANCE */}
        <section className="py-12 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4 max-w-4xl text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-6">Proudly Serving Longview, TX & East Texas</h2>
            <p className="text-lg text-primary-foreground/90 leading-relaxed">
              At Woods Spine & Wellness, we are deeply committed to bringing the most advanced, non-invasive healthcare technologies to our local community in Longview, TX, and the greater East Texas region. We believe our neighbors deserve access to world-class regenerative therapies like SoftWave TRT without having to travel to major metropolitan areas. Whether you are an active senior looking to maintain mobility, a local athlete recovering from an injury, or a hardworking professional dealing with chronic pain, our dedicated team is here to help you get back to a better you, right here at home.
            </p>
          </div>
        </section>

        {/* FAQ SECTION */}
        <section className="py-16">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-8 text-center">Frequently Asked Questions</h2>
            <div className="space-y-8">
              <div className="border-b border-border pb-6">
                <h3 className="text-xl font-bold text-primary mb-3">Is SoftWave TRT safe?</h3>
                <p className="text-muted-foreground text-base leading-relaxed">
                  Yes, SoftWave TRT is incredibly safe. It is an FDA-cleared technology that has been used successfully for years with no known significant side effects. Because it is non-invasive and does not rely on medications or injections, the risks associated with traditional surgeries or long-term pharmaceutical use are completely eliminated.
                </p>
              </div>
              <div className="border-b border-border pb-6">
                <h3 className="text-xl font-bold text-primary mb-3">How long until I see results?</h3>
                <p className="text-muted-foreground text-base leading-relaxed">
                  Many patients experience a noticeable decrease in pain and an increase in mobility immediately following their very first session. However, the true regenerative effects—such as stem cell recruitment and tissue repair—take place over the following 8 to 12 weeks. Maximum results are typically seen a few months after completing the full treatment protocol.
                </p>
              </div>
              <div className="border-b border-border pb-6">
                <h3 className="text-xl font-bold text-primary mb-3">Is SoftWave covered by insurance?</h3>
                <p className="text-muted-foreground text-base leading-relaxed">
                  SoftWave TRT is considered an elective, cutting-edge regenerative therapy and is currently not covered by major medical insurance or Medicare. However, we believe in making this transformative care accessible. We offer competitive pricing, new patient specials for your initial consultation and first treatment, and flexible payment options.
                </p>
              </div>
              <div className="border-b border-border pb-6">
                <h3 className="text-xl font-bold text-primary mb-3">Does the treatment hurt?</h3>
                <p className="text-muted-foreground text-base leading-relaxed">
                  The treatment is generally not painful. You will feel a pulsing sensation during the procedure. If the applicator passes over an area of significant tissue damage or inflammation, you may feel a mild, deep ache or "zing." This is actually a positive sign, as it helps our doctors identify the exact location that needs healing. The intensity can always be adjusted to ensure your comfort.
                </p>
              </div>
              <div className="pb-2">
                <h3 className="text-xl font-bold text-primary mb-3">How is SoftWave different from ultrasound or laser therapy?</h3>
                <p className="text-muted-foreground text-base leading-relaxed">
                  While ultrasound and laser therapies are beneficial for superficial inflammation, SoftWave's electrohydraulic acoustic waves penetrate much deeper into the tissue (up to several inches). Furthermore, SoftWave is unique in its ability to physically stimulate the recruitment and activation of your body's own stem cells, leading to true tissue regeneration rather than just temporary symptom relief.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-20 bg-secondary/30 text-center border-t border-border">
          <div className="container mx-auto px-4 max-w-3xl">
            <h2 className="text-3xl md:text-4xl font-bold text-primary mb-6">Ready to Experience SoftWave?</h2>
            <p className="text-xl text-muted-foreground mb-8">
              Take the first step toward living pain-free. Schedule your SoftWave consultation and initial treatment today to see if you are a candidate.
            </p>
            <Button size="lg" className="bg-accent text-white hover:bg-accent/90 border-0" asChild>
              <Link to="/appointment-scheduling">
                Book Your Appointment Now <ChevronRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </section>

      </main>
      <Footer />
    </div>
  );
}
